/**
 * TypeScript type definitions for the Flow Engine
 * Matches Help Flow Schema v1.0-1.2 with React adaptations
 */

/**
 * Main flow configuration interface
 */
export interface Flow {
  /** Schema version for compatibility checking */
  schemaVersion: string;
  /** Backwards compatibility marker */
  compatibleWith?: string;
  /** Unique flow identifier */
  id: string;
  /** Human-readable flow title */
  title?: string;
  /** Flow name (legacy) */
  name?: string;
  /** Flow description */
  description?: string;
  /** Flow version */
  version?: string;
  /** Tags for categorization */
  tags?: string[];
  /** Reusable step definitions */
  reusable?: Record<string, Step>;
  /** Main flow steps */
  steps: Step[];
  /** Maximum steps before termination */
  maxSteps?: number;
  /** Maximum execution time in milliseconds */
  maxExecutionTime?: number;
}

/**
 * Individual step configuration
 */
export interface Step {
  /** Unique step identifier */
  id: string;
  /** Step type determining behavior */
  type: StepType;
  /** Static content to display */
  content?: string;
  /** Legacy text property (use content instead) */
  text?: string;
  /** Image URL or path */
  image?: string;
  /** Next step ID for normal flow */
  next?: string;
  /** Next step ID for error handling */
  errorNext?: string;
  /** Choice chips for user interaction */
  chips?: Chip[];
  /** Variables to sync with Storyline */
  syncStoryline?: Record<string, any>;
  /** Prompt for LLM/Vision steps */
  prompt?: string;
  /** Variable name to store image input */
  imageVar?: string;
  /** Variable name to store step result */
  storeAs?: string;
  /** Input validation rules */
  validation?: ValidationRule;
  /** Input placeholder text */
  placeholder?: string;
  /** Content template for dynamic rendering */
  template?: string;
  /** Step-specific variables */
  variables?: Record<string, any>;
  /** Loading message for async operations */
  loadingMessage?: string;
  /** Delay before auto-advance (ms) */
  delayMs?: number;
  /** Whether input is required */
  required?: boolean;
  /** Skip step if variable already exists */
  skipIfExists?: boolean;
  /** Input UI visibility configuration */
  inputVisibility?: 'both' | 'text' | 'plus' | 'none';
}

/**
 * Supported step types
 */
export type StepType =
  | 'system'      // Display message, auto-advance
  | 'chips'       // Present choice options
  | 'textInput'   // Request text input
  | 'imageInput'  // Request image upload
  | 'llm'         // LLM API call
  | 'vision'      // Vision API call
  | 'imageEdit'   // Image editing API call
  | 'helpdesk'    // Helpdesk integration
  | 'logic'       // Conditional logic (not supported)
  | 'reusable';   // Reference to reusable step

/**
 * User choice chip configuration
 */
export interface Chip {
  /** Unique chip identifier */
  id: string;
  /** Display label */
  label: string;
  /** Next step ID */
  next?: string;
  /** Action to trigger */
  action?: string;
  /** Transform chip to user message */
  transformToUser?: boolean;
  /** Variables to set when selected */
  setVars?: Record<string, any>;
  /** Variables to sync with Storyline */
  syncStoryline?: Record<string, any>;
}

/**
 * Input validation configuration
 */
export interface ValidationRule {
  /** Regex pattern for text validation */
  pattern?: string;
  /** Minimum text length */
  minLength?: number;
  /** Maximum text length */
  maxLength?: number;
  /** Custom validation error message */
  message?: string;
  /** Allowed file types for uploads */
  fileTypes?: string[];
  /** Maximum file size in bytes */
  maxSize?: number;
}

/**
 * Flow execution state
 */
export interface FlowState {
  /** The current flow */
  flow: Flow | null;
  /** Currently active step */
  currentStep: Step | null;
  /** Flow variables store */
  variables: Record<string, any>;
  /** Number of steps executed */
  stepCount: number;
  /** Flow start timestamp */
  startTime: number;
  /** Current execution status */
  status: 'idle' | 'running' | 'completed' | 'error';
  /** Step execution history */
  history: string[];
}

/**
 * Flow engine configuration options
 */
export interface FlowEngineOptions {
  /** Initial variable values */
  initialVariables?: Record<string, any>;
  /** Maximum steps before termination */
  maxSteps?: number;
  /** Maximum execution time in milliseconds */
  maxExecutionTime?: number;
}

/**
 * Typed event map for flow engine events
 */
export type FlowEventMap = {
  /** Step transition event */
  stepChanged: { from: Step | null; to: Step };
  /** Variable update event */
  variableSet: { name: string; value: any };
  /** Variable changed event (alias for variableSet) */
  variableChanged: { key: string; value: any };
  /** Storyline synchronization event */
  storylineSync: { variables: Record<string, any> };
  /** Chip action event */
  chipAction: { action: string; data?: any };
  /** Flow completion event */
  flowCompleted: { finalStep: Step };
  /** Flow started event */
  flowStarted: { flow: Flow };
  /** Flow finished event */
  flowFinished: { flow: Flow };
  /** Error event */
  error: { type: string; message: string; details?: any };
  /** Input request event */
  inputRequested: { type: 'text' | 'image'; config: any; step: Step };
  /** Input completion event */
  inputComplete: { type: 'text' | 'image'; value?: string; file?: File };
};

/**
 * Flow event type union
 */
export type FlowEventType = keyof FlowEventMap;

/**
 * Type-safe event handler
 */
export type FlowEventHandler<T extends FlowEventType> = (event: FlowEventMap[T]) => void;

/**
 * Custom error class for flow operations
 */
export class FlowError extends Error {
  constructor(
    public type: string,
    message: string,
    public details?: any,
  ) {
    super(message);
    this.name = 'FlowError';
  }
}

/**
 * Built-in flow variables
 */
export interface BuiltInVariables {
  /** Last user text input */
  lastUserText?: string;
  /** Last uploaded image URL */
  lastImage?: string;
  /** Current step ID */
  currentStep?: string;
  /** Current flow ID */
  flowId?: string;
}

/**
 * Flow loading configuration
 */
export interface FlowLoadOptions {
  /** Base URL for flow assets */
  baseUrl?: string;
  /** Cache flows in memory */
  enableCache?: boolean;
  /** Validation mode */
  validation?: 'strict' | 'loose' | 'none';
}

/**
 * Variable resolver configuration
 */
export interface VariableResolverOptions {
  /** Default value for missing variables */
  defaultValue?: string;
  /** Throw error on missing variables */
  throwOnMissing?: boolean;
  /** Custom variable transformers */
  transformers?: Record<string, (value: any) => string>;
}